/**
 * shared/components/PropertyCard.jsx
 * -----------------------------------
 * The single, reusable horizontal property card for the entire platform.
 *
 *   <PropertyCard
 *     property={p}
 *     variant="admin" | "dealer" | "builder" | "team"
 *     selected={bool}
 *     selectionMode={bool}     // when true, tap toggles instead of opening
 *     onToggleSelect={(id)=>}
 *     onOpen={(p)=>}           // tap card body (when not in select mode)
 *     onEdit, onDelete, onMarkSold, onMarkAvailable,
 *     onToggleFeatured, onResubmit, onShortlist,
 *     showImage={true}, compact={false},
 *     testidPrefix="pc"
 *   />
 *
 * Layout (works at 320px+, optimal at 390px+):
 *
 *   ┌──────┬─────────────────────────────────────────┐
 *   │      │ Title          [tags / status badges]   │
 *   │  img │ area · bhk · type                       │
 *   │      │ ₹ price · ROI · metro                   │
 *   ├──────┴──────────────────────────────────────────┤
 *   │ [inline actions row — role-specific]           │
 *   └─────────────────────────────────────────────────┘
 *
 * v2.71.53 — Initial. Replaces:
 *   - Admin listings <table> rows
 *   - Dealer submissions text-only divs
 *   - Builder listings text-only divs
 * Wired into TeamPropertiesPage via variant="team" in v2.71.52.
 */

import React from "react";
import { Button } from "../../components/ui/button";
import {
  Building2, Star, MapPin, CheckCircle, Pencil, Trash2, Send,
  Package, Lock, AlertTriangle, ShieldCheck, Check, X,
  ListPlus, ExternalLink,
} from "lucide-react";
import { getPropertyThumbnail } from "../utils/property";
import { normalizePropertyType, normalizeBhk } from "../utils/propertyNormalize";

const fmtINR = (n) => {
  const num = Number(n) || 0;
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000)   return `₹${(num / 100000).toFixed(2)} L`;
  if (num >= 1000)     return `₹${(num / 1000).toFixed(1)}K`;
  return `₹${num}`;
};

// Approval status visual map (for dealer/builder variants)
const APPROVAL_VISUAL = {
  pending:  { label: "Pending",  cls: "bg-amber-50 text-amber-800 border-amber-200" },
  approved: { label: "Approved", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  rejected: { label: "Rejected", cls: "bg-rose-50 text-rose-800 border-rose-200" },
  draft:    { label: "Draft",    cls: "bg-slate-50 text-slate-700 border-slate-200" },
};

export default function PropertyCard({
  property: p,
  variant = "admin",
  selected = false,
  selectionMode = false,
  onToggleSelect = null,
  onOpen = null,
  // admin
  onEdit = null,
  onDelete = null,
  onMarkSold = null,
  onMarkAvailable = null,
  onToggleFeatured = null,
  // dealer / builder
  onResubmit = null,
  // shared / team
  onShortlist = null,
  showImage = true,
  compact = false,
  testidPrefix = "pc",
}) {
  if (!p) return null;

  const cover = getPropertyThumbnail(p);
  const tid = `${testidPrefix}-${p.id}`;
  const price = p.price || p.monthly_rent || 0;
  const area = p.main_area || p.area || "—";
  const subArea = p.sub_area;
  // v2.78 Phase A — Canonical display values. Raw DB stays untouched.
  const bhkRaw = normalizeBhk(p.bhk);
  const bhk = bhkRaw && bhkRaw !== "Not Specified" ? bhkRaw : "";
  const propertyTypeDisplay = normalizePropertyType(p.property_type);
  const isSold = p.status === "Sold";
  const isPending = p.approval_status === "pending";
  const isRejected = p.approval_status === "rejected";
  const isDraft = p.approval_status === "draft";

  const handleCardClick = () => {
    if (selectionMode && onToggleSelect) {
      onToggleSelect(p.id);
      return;
    }
    if (onOpen) onOpen(p);
  };

  const stop = (e) => e.stopPropagation();

  const approvalBadge = (variant === "dealer" || variant === "builder") && p.approval_status
    ? (APPROVAL_VISUAL[p.approval_status] || APPROVAL_VISUAL.pending)
    : null;

  return (
    <div
      data-testid={tid}
      data-selected={selected ? "true" : "false"}
      onClick={handleCardClick}
      role={onOpen || selectionMode ? "button" : undefined}
      tabIndex={onOpen || selectionMode ? 0 : undefined}
      onKeyDown={(e) => {
        if ((onOpen || selectionMode) && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          handleCardClick();
        }
      }}
      className={`bg-white border rounded-xl overflow-hidden transition-all hover:border-slate-300 active:scale-[0.995] ${
        selected ? "border-indigo-500 ring-2 ring-indigo-200" : "border-slate-200"
      } ${onOpen || selectionMode ? "cursor-pointer" : ""}`}
    >
      <div className={`flex items-stretch gap-3 ${compact ? "p-2" : "p-3"}`}>
        {/* Selection checkbox */}
        {selectionMode && (
          <div className="flex items-start pt-1">
            <button
              onClick={(e) => { stop(e); onToggleSelect?.(p.id); }}
              data-testid={`${tid}-check`}
              aria-label={selected ? "Deselect" : "Select"}
              className={`shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center ${
                selected ? "bg-indigo-600 border-indigo-600" : "border-slate-300 bg-white hover:border-indigo-400"
              }`}
            >
              {selected && <Check size={12} className="text-white" />}
            </button>
          </div>
        )}

        {/* Cover image */}
        {showImage && (
          <div
            className={`shrink-0 rounded-lg bg-slate-100 overflow-hidden relative ${
              compact ? "w-16 h-16" : "w-20 h-20 sm:w-24 sm:h-24"
            }`}
            data-testid={`${tid}-thumb`}
          >
            {/* Building-icon placeholder always renders behind the <img>; image overlays it when it loads successfully.
                On <img> error we hide the <img> via display:none, revealing the icon below. */}
            <div className="absolute inset-0 flex items-center justify-center text-slate-400">
              <Building2 size={compact ? 16 : 20} />
            </div>
            {cover ? (
              <img
                src={cover}
                alt={p.title || "Property"}
                loading="lazy"
                decoding="async"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : null}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title row */}
          <div className="flex items-start justify-between gap-2">
            <h4
              data-testid={`${tid}-title`}
              className={`font-semibold text-slate-900 leading-tight ${compact ? "text-[13px]" : "text-[14px]"} line-clamp-2`}
            >
              {p.title}
            </h4>
            {/* Featured star (admin only) */}
            {variant === "admin" && (
              <button
                data-testid={`${tid}-featured-toggle`}
                onClick={(e) => { stop(e); onToggleFeatured?.(p); }}
                disabled={!onToggleFeatured}
                title={p.is_featured ? "Unfeature" : "Mark Featured"}
                className={`shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md border transition-all ${
                  p.is_featured
                    ? "bg-amber-50 text-amber-700 border-amber-300"
                    : "bg-white text-slate-400 border-slate-200 hover:border-amber-300 hover:text-amber-600"
                } disabled:opacity-50`}
                aria-label={p.is_featured ? "Unfeature" : "Mark Featured"}
              >
                <Star size={12} fill={p.is_featured ? "#F59E0B" : "none"} />
              </button>
            )}
          </div>

          {/* Meta line */}
          <div className={`text-slate-500 mt-0.5 flex items-center gap-1 ${compact ? "text-[11px]" : "text-[12px]"}`}>
            <MapPin size={11} className="shrink-0" />
            <span className="truncate">
              {area}
              {subArea && ` · ${subArea}`}
              {bhk && ` · ${bhk}`}
              {propertyTypeDisplay && ` · ${propertyTypeDisplay}`}
            </span>
          </div>

          {/* Price + stats */}
          <div className={`mt-1 flex items-center gap-2 flex-wrap ${compact ? "text-[11.5px]" : "text-[12.5px]"}`}>
            <span className="font-semibold text-indigo-700 font-mono-data" data-testid={`${tid}-price`}>
              {fmtINR(price)}
            </span>
            {p.roi != null && p.roi > 0 && (
              <span className="text-emerald-700 font-mono-data text-[11px]">{Number(p.roi).toFixed(1)}% ROI</span>
            )}
            {p.distance_metro_m != null && variant === "admin" && (
              <span className="text-slate-500 text-[11px]">{p.distance_metro_m}m metro</span>
            )}
          </div>

          {/* Tags / Status badges */}
          <div className="mt-1.5 flex items-center gap-1 flex-wrap">
            {approvalBadge && (
              <span
                data-testid={`${tid}-status`}
                className={`inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 border rounded ${approvalBadge.cls}`}
              >
                {approvalBadge.label}
              </span>
            )}
            {isSold && (
              <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 border rounded bg-slate-900 text-white border-slate-900">
                <CheckCircle size={9} /> Sold
              </span>
            )}
            {p.is_featured && variant !== "admin" && (
              <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 border rounded bg-amber-50 text-amber-800 border-amber-200">
                <Star size={9} /> Featured
              </span>
            )}
            {p.best_deal && (
              <span className="text-[10px] font-semibold text-amber-700">★ Deal</span>
            )}
            {(p.premium_deal || p.priority) && (
              <span className="text-[10px] font-semibold text-indigo-700">⬡ Premium</span>
            )}
            {p.assigned_by_admin && (variant === "dealer" || variant === "builder") && (
              <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 border rounded bg-indigo-50 text-indigo-700 border-indigo-200" title="Assigned by admin">
                <ShieldCheck size={9} /> Assigned
              </span>
            )}
            {p.locked_fields && p.locked_fields.length > 0 && (variant === "dealer" || variant === "builder") && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 border rounded bg-slate-50 text-slate-600 border-slate-200" title={`Locked: ${p.locked_fields.join(", ")}`}>
                <Lock size={9} /> {p.locked_fields.length} locked
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Rejection reason (dealer/builder) */}
      {isRejected && p.rejection_reason && (variant === "dealer" || variant === "builder") && (
        <div
          className="mx-3 mb-2 bg-rose-50 border border-rose-200 rounded-md p-2"
          data-testid={`${tid}-rejection`}
          onClick={stop}
        >
          <div className="text-[10px] uppercase tracking-wider font-semibold text-rose-800 inline-flex items-center gap-1 mb-0.5">
            <AlertTriangle size={9} /> Admin feedback
          </div>
          <div className="text-[12px] text-rose-900">{p.rejection_reason}</div>
        </div>
      )}

      {/* Inline action row */}
      {!selectionMode && hasActions(variant, { onEdit, onDelete, onMarkSold, onMarkAvailable, onResubmit, onShortlist, isSold, isPending, isRejected, isDraft }) && (
        <div
          className="px-3 pb-2.5 pt-1 flex items-center gap-1.5 flex-wrap border-t border-slate-100"
          onClick={stop}
          data-testid={`${tid}-actions`}
        >
          {/* Admin actions */}
          {variant === "admin" && (
            <>
              {!isSold && onMarkSold && (
                <Button size="sm" variant="ghost" onClick={() => onMarkSold(p.id)} data-testid={`${tid}-mark-sold`} className="h-7 px-2 text-[11.5px]" title="Mark Sold">
                  <CheckCircle size={11} className="text-emerald-600 mr-1" /> Sold
                </Button>
              )}
              {isSold && onMarkAvailable && (
                <Button size="sm" variant="ghost" onClick={() => onMarkAvailable(p.id)} data-testid={`${tid}-mark-available`} className="h-7 px-2 text-[11.5px]" title="Mark Available">
                  <Package size={11} className="mr-1" /> Available
                </Button>
              )}
              {onEdit && (
                <Button size="sm" variant="ghost" onClick={() => onEdit(p)} data-testid={`${tid}-edit`} className="h-7 px-2 text-[11.5px]" title="Edit">
                  <Pencil size={11} className="mr-1" /> Edit
                </Button>
              )}
              {onDelete && (
                <Button size="sm" variant="ghost" onClick={() => onDelete(p.id)} data-testid={`${tid}-delete`} className="h-7 px-2 text-[11.5px] text-rose-600 hover:bg-rose-50" title="Delete">
                  <Trash2 size={11} className="mr-1" /> Delete
                </Button>
              )}
            </>
          )}

          {/* Dealer / Builder actions */}
          {(variant === "dealer" || variant === "builder") && (
            <>
              {(isDraft || isRejected) && onResubmit && (
                <Button
                  size="sm"
                  onClick={() => onResubmit(p.id)}
                  data-testid={`${tid}-resubmit`}
                  className="h-7 px-2 text-[11.5px] bg-indigo-600 hover:bg-indigo-700"
                >
                  <Send size={11} className="mr-1" /> {isRejected ? "Resubmit" : "Submit"}
                </Button>
              )}
              {onEdit && (
                <Button size="sm" variant="outline" onClick={() => onEdit(p)} data-testid={`${tid}-edit`} className="h-7 px-2 text-[11.5px]" title="Edit listing">
                  <Pencil size={11} className="mr-1" /> Edit
                </Button>
              )}
              {onDelete && (
                <Button size="sm" variant="ghost" onClick={() => onDelete(p.id)} data-testid={`${tid}-delete`} className="h-7 px-2 text-[11.5px] text-rose-600 hover:bg-rose-50" title="Delete">
                  <Trash2 size={11} className="mr-1" /> Delete
                </Button>
              )}
            </>
          )}

          {/* Team actions */}
          {variant === "team" && (
            <>
              {onShortlist && (
                <Button size="sm" variant="outline" onClick={() => onShortlist(p)} data-testid={`${tid}-shortlist`} className="h-7 px-2 text-[11.5px]">
                  <ListPlus size={11} className="mr-1" /> Shortlist
                </Button>
              )}
              {onOpen && (
                <Button size="sm" variant="ghost" onClick={() => onOpen(p)} data-testid={`${tid}-open`} className="h-7 px-2 text-[11.5px]">
                  <ExternalLink size={11} className="mr-1" /> Open
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function hasActions(variant, h) {
  if (variant === "admin") return !!(h.onEdit || h.onDelete || h.onMarkSold || h.onMarkAvailable);
  if (variant === "dealer" || variant === "builder") return !!(h.onEdit || h.onDelete || h.onResubmit);
  if (variant === "team") return !!(h.onShortlist);
  return false;
}
